import React, { useState } from "react";
import { Card, FormGroup, Label, Button, Spinner } from "reactstrap";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  Elements,
  ElementsConsumer,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { makeStripePayment } from "store/actions/stripeAction";
import { useHolidayGuard } from "hooks/useHolidayGuard";
import { addPaymentAction } from "store/actions/orderActions";

const StripePayment = ({
  totalAmount,
  currency,
  restaurantsDetail,
  isHoliday,
  stripeClientKey,
  stripeFees = 0,
  stripeFeesType = "fixed_price",
  stripeAmount = 0,
  confirmOrder,
}) => {
  const dispatch = useDispatch();
  let stripePromise = loadStripe(stripeClientKey);
  const { user } = useSelector((state) => state.auth);

  const [paymentLoader, setPaymentLoader] = useState(false);
  const restaurantID = user?.restaurantID;

  const { holidayStatus, checkAndToast } = useHolidayGuard(
        restaurantsDetail,
        "dinein"
      );
  const handleSubmit = async (e, elements, stripe) => {
    e.preventDefault();

    if (checkAndToast()) return;
    if (!stripe || !elements) {
      return;
    }

    setPaymentLoader(true);

    try {
      await dispatch(
        makeStripePayment(
          stripe,
          elements,
          stripeAmount,
          restaurantID,
          user?.email
        )
      );

      const orderID = await confirmOrder("stripe", true);
      if (orderID) {
        const paymentObject = {
          orderId: orderID,
          paymentMethod: "stripe",
          amount: (stripeAmount || 0).toString(),
          status: "status",
          uid: user?.id,
        };
        dispatch(addPaymentAction(paymentObject));
      }
    } catch (err) {
      toast.error("Payment failed");
      console.error(err?.response?.data?.message || err.message, "Error");

      if (elements) {
        elements.getElement(CardNumberElement)?.clear();
        elements.getElement(CardExpiryElement)?.clear();
        elements.getElement(CardCvcElement)?.clear();
      }
    } finally {
      setPaymentLoader(false);
    }
  };

  return (
    <Card
      className="p-3 mb-3 cart__payment_card"
      style={{ border: "1px solid rgba(0, 0, 0, .125)" }}
    >
      {/* Using Dummy value for now */}
      <Elements stripe={stripePromise}>
        <ElementsConsumer>
          {({ elements, stripe }) => {
            return (
              <form onSubmit={(e) => handleSubmit(e, elements, stripe)}>
                <FormGroup>
                  <Label for="cardNumber" className="fs--14 text-dark">
                    Card Number:
                  </Label>
                  <div className="form-control mt-2 d-flex justify-content-between align-items-center shadow-sm">
                    <div style={{ flexBasis: "96%" }}>
                      <CardNumberElement
                        options={{
                          color: "#000",
                          placeholder: "XXXX XXXX XXXX XXXX",
                          style: {
                            base: {
                              fontSize: "16px",
                            },
                            invalid: {
                              color: "#9e2146",
                            },
                          },
                        }}
                      />
                    </div>
                  </div>
                </FormGroup>
                <FormGroup>
                  <Label for="expiryDate" className="fs--14 text-dark">
                    Expiry Date:
                  </Label>
                  <div className="form-control mt-2 d-flex justify-content-between align-items-center shadow-sm">
                    <div style={{ flexBasis: "96%" }}>
                      <CardExpiryElement
                        options={{
                          color: "#000",
                          placeholder: "MM/YY",
                          style: {
                            base: {
                              fontSize: "16px",
                            },
                            invalid: {
                              color: "#9e2146",
                            },
                          },
                        }}
                      />
                    </div>
                  </div>
                </FormGroup>
                <FormGroup>
                  <Label for="cvv" className="fs--14 text-dark">
                    CVC/CVV:
                  </Label>
                  <div className="form-control mt-2 d-flex justify-content-between align-items-center shadow-sm">
                    <div style={{ flexBasis: "96%" }}>
                      <CardCvcElement
                        options={{
                          color: "#000",
                          placeholder: "...",
                          style: {
                            color: "#000",
                            base: {
                              fontSize: "16px",
                            },
                            invalid: {
                              color: "#9e2146",
                            },
                          },
                        }}
                      />
                    </div>
                  </div>
                </FormGroup>

                <div className="d-flex justify-content-center my-2 text-dark">
                  <span className="fs--20 fw--400 mr-2">Tax Fees:</span>
                  <span className="fs--20 fw--400">
                    &nbsp;
                    {stripeFeesType === "percentage" ? "%" : currency}
                    {stripeFees || 0}
                  </span>
                </div>
                <div className="d-flex justify-content-center my-1 text-dark">
                  <span className="fs--20 fw--400 mr-1">Total Amount:</span>
                  <span className="fs--20 fw--400">
                    {currency}&nbsp;
                    {stripeAmount ? stripeAmount?.toFixed(2) : 0}
                  </span>
                </div>

                <Button
                  className="auth-button mt-2 py-3 btn btn-lg btn-block border border-none"
                  type="submit"
                  style={{ backgroundColor: "rgb(243, 243, 20)" }}
                  disabled={paymentLoader}
                >
                  {paymentLoader ? (
                    <Spinner size="sm" color="white" />
                  ) : (
                    <h4 className="mb-0 fs--20 text-white">Pay with Stripe</h4>
                  )}
                </Button>
              </form>
            );
          }}
        </ElementsConsumer>
      </Elements>
    </Card>
  );
};

export default StripePayment;
